import { registerTextureCacheSizeProvider } from '@utils/debug/webglDiagnostics'
import { Material, SRGBColorSpace, Texture } from 'three'
import { isLightweightViewer } from '../../viewport/readCapabilities'
import {
  cheapenFinishTexture,
  clampTextureImageToGpuBudget,
  closeGpuImageSource,
  closeTextureGpuImage,
  downscaleImageForGpu,
  isUploadableTextureImage,
} from './clampGpuTexture'

/** Albedo maps deben estar en sRGB para PBR correcto (evita texturas lavadas). */
export function configureColorTexture(texture: Texture): void {
  texture.colorSpace = SRGBColorSpace
}

export const MESH_OWNED_TEXTURE_CLONE = '__meshOwnedClone'

/**
 * Clone with its own repeat/offset. Shares the decoded Source / GPU image.
 * Do not set needsUpdate: that uploads a second WebGLTexture in Safari.
 */
export function cloneCachedTextureForMesh(texture: Texture): Texture {
  const clone = texture.clone()
  clone.colorSpace = texture.colorSpace
  clone.anisotropy = texture.anisotropy
  clone.generateMipmaps = texture.generateMipmaps
  clone.minFilter = texture.minFilter
  clone.magFilter = texture.magFilter
  clone.userData[MESH_OWNED_TEXTURE_CLONE] = true
  return clone
}

const MAX_CACHED_TEXTURES = 512
const MAX_CACHED_TEXTURES_COMPACT = 96
const LOG_PREFIX = '[TextureCache]'

type CacheEntry = {
  texture: Texture
  refCount: number
  lastUsed: number
  status: 'loading' | 'ready'
  pendingLoads: Array<(texture: Texture) => void>
}

const byUrl = new Map<string, CacheEntry>()
const urlByTexture = new WeakMap<Texture, string>()

export function normalizeTextureUrl(url: string): string {
  if (!url) return url
  try {
    const parsed = new URL(url, window.location.origin)
    parsed.searchParams.delete('not-from-cache-please')
    return parsed.toString()
  } catch {
    return url.replace(/[?&]not-from-cache-please/, '').replace(/\?$/, '')
  }
}

/** URL de fetch con cache-bust (comportamiento legacy del loader). */
function buildFetchUrl(cacheKey: string): string {
  return cacheKey.includes('?')
    ? `${cacheKey}&not-from-cache-please`
    : `${cacheKey}?not-from-cache-please`
}

function touch(entry: CacheEntry) {
  entry.lastUsed = performance.now()
}

export function evictUnreferencedTextures(options?: { forceAll?: boolean }) {
  const maxCached = options?.forceAll
    ? 0
    : isLightweightViewer()
      ? MAX_CACHED_TEXTURES_COMPACT
      : MAX_CACHED_TEXTURES
  if (byUrl.size <= maxCached) return

  const candidates = [...byUrl.entries()]
    .filter(([, entry]) => entry.refCount <= 0 && entry.status === 'ready')
    .sort((a, b) => a[1].lastUsed - b[1].lastUsed)

  for (const [key, entry] of candidates) {
    if (byUrl.size <= maxCached) break
    closeTextureGpuImage(entry.texture)
    entry.texture.dispose()
    byUrl.delete(key)
    if (import.meta.env?.DEV) {
      console.debug(`${LOG_PREFIX} evicted texture`)
    }
  }
}

function flushPendingLoads(entry: CacheEntry) {
  const callbacks = entry.pendingLoads.splice(0)
  for (const cb of callbacks) {
    cb(entry.texture)
  }
}

export function retainTexture(texture: Texture | null | undefined) {
  if (!texture) return
  const key = urlByTexture.get(texture)
  if (!key) return
  const entry = byUrl.get(key)
  if (!entry || entry.status !== 'ready') return
  entry.refCount++
  touch(entry)
}

export function releaseTexture(texture: Texture | null | undefined) {
  if (!texture) return

  const key = urlByTexture.get(texture)
  if (key) {
    const entry = byUrl.get(key)
    if (!entry) return
    entry.refCount = Math.max(0, entry.refCount - 1)
    touch(entry)
    evictUnreferencedTextures()
    return
  }

  // Unique mesh clone (repeat/offset). Do not close the shared bitmap.
  if (texture.userData?.[MESH_OWNED_TEXTURE_CLONE]) {
    texture.dispose()
    return
  }

  // GLB / catalog maps are shared across units. Disposing them here is what
  // makes texSubImage2D fail after a few minutes on the compact viewer.
}

export function setMaterialMap(material: Material, texture: Texture | null) {
  const previous = material.map
  if (previous && previous !== texture) {
    releaseTexture(previous)
    releaseCachedTextureBase(material)
  }
  material.map = texture
  if (texture) {
    retainTexture(texture)
  }
  material.needsUpdate = true
}

/** Libera la referencia al texture base del caché guardada en userData. */
export function releaseCachedTextureBase(material: Material) {
  const base = material.userData?.currentTextureBase as Texture | undefined
  if (base) {
    releaseTexture(base)
    material.userData.currentTextureBase = null
  }
}

/** Textura clonada con repeat/offset propio: retiene el base del caché mientras el clone esté activo. */
export function setMaterialMapUnique(material: Material, texture: Texture, baseTexture?: Texture) {
  const previous = material.map
  if (previous && previous !== texture) {
    releaseTexture(previous)
    releaseCachedTextureBase(material)
  }
  material.map = texture
  if (baseTexture) {
    retainTexture(baseTexture)
    material.userData.currentTextureBase = baseTexture
  }
  material.needsUpdate = true
}

export function getCachedTexture(url: string): Texture | undefined {
  const key = normalizeTextureUrl(url)
  const entry = byUrl.get(key)
  if (!entry || entry.status !== 'ready') return undefined
  touch(entry)
  return entry.texture
}

export function setCachedTexture(url: string, texture: Texture) {
  clampTextureImageToGpuBudget(texture)
  const key = normalizeTextureUrl(url)
  const existing = byUrl.get(key)
  if (existing) {
    if (existing.texture !== texture) {
      if (existing.status === 'ready') {
        releaseTexture(existing.texture)
      } else {
        existing.texture.dispose()
      }
      existing.texture = texture
      existing.refCount = 0
      existing.status = 'ready'
      existing.pendingLoads = []
    }
    touch(existing)
    urlByTexture.set(texture, key)
    return
  }
  byUrl.set(key, {
    texture,
    refCount: 0,
    lastUsed: performance.now(),
    status: 'ready',
    pendingLoads: [],
  })
  urlByTexture.set(texture, key)
  evictUnreferencedTextures()
}

export function loadCachedTexture(url: string, onLoad: (texture: Texture) => void): Texture | null {
  const key = normalizeTextureUrl(url)
  if (!key) return null

  const existing = byUrl.get(key)
  if (existing?.status === 'ready') {
    touch(existing)
    onLoad(existing.texture)
    return existing.texture
  }
  if (existing?.status === 'loading') {
    existing.pendingLoads.push(onLoad)
    return existing.texture
  }

  const fetchUrl = buildFetchUrl(key)
  const pendingLoads: Array<(texture: Texture) => void> = [onLoad]

  const texture = new Texture()
  cheapenFinishTexture(texture)
  configureColorTexture(texture)

  // Registrar ANTES de que termine la carga para evitar loads duplicados concurrentes.
  byUrl.set(key, {
    texture,
    refCount: 0,
    lastUsed: performance.now(),
    status: 'loading',
    pendingLoads,
  })
  urlByTexture.set(texture, key)

  const image = new Image()
  image.crossOrigin = 'anonymous'
  image.onload = () => {
    const entry = byUrl.get(key)
    if (!entry) return
    void downscaleImageForGpu(image)
      .then(({ image: gpuImage, clampedFrom }) => {
        const current = byUrl.get(key)
        if (!current) {
          closeGpuImageSource(gpuImage)
          return
        }
        current.texture.image = gpuImage
        cheapenFinishTexture(current.texture)
        configureColorTexture(current.texture)
        current.texture.userData.__gpuSizeClamped = true
        if (clampedFrom) current.texture.userData.__gpuClampFrom = clampedFrom
        current.texture.needsUpdate = true
        current.status = 'ready'
        touch(current)
        urlByTexture.set(current.texture, key)
        flushPendingLoads(current)
      })
      .catch((error) => {
        console.error(`${LOG_PREFIX} failed to clamp`, key, error)
        const current = byUrl.get(key)
        if (!current) return
        current.pendingLoads = []
        closeTextureGpuImage(current.texture)
        current.texture.dispose()
        byUrl.delete(key)
      })
  }
  image.onerror = (error) => {
    console.error(`${LOG_PREFIX} failed to load`, key, error)
    const entry = byUrl.get(key)
    if (entry) {
      entry.pendingLoads = []
      closeTextureGpuImage(entry.texture)
      entry.texture.dispose()
      byUrl.delete(key)
    }
  }
  image.src = fetchUrl

  evictUnreferencedTextures()

  return texture
}

export function getTextureCacheSize(): number {
  return byUrl.size
}

export function getTextureCacheMemoryEstimate() {
  const top: Array<{ url: string; w: number; h: number; mb: number; clampedFrom?: string }> = []
  let mb = 0
  for (const [url, entry] of byUrl) {
    const img = entry.texture.image as { width?: number; height?: number } | undefined
    const w = img?.width || 0
    const h = img?.height || 0
    const bytes = w * h * 4 * (entry.texture.generateMipmaps ? 1.33 : 1)
    const sizeMB = bytes / (1024 * 1024)
    mb += sizeMB
    if (w && h) {
      top.push({
        url,
        w,
        h,
        mb: Number(sizeMB.toFixed(2)),
        clampedFrom: entry.texture.userData?.__gpuClampFrom,
      })
    }
  }
  top.sort((a, b) => b.mb - a.mb)
  return { count: byUrl.size, mb: Number(mb.toFixed(2)), top: top.slice(0, 12) }
}

/** After WebGL context restore: keep JS images, force a new GPU upload. */
export function markCachedTexturesNeedsUpdate() {
  for (const [, entry] of byUrl) {
    if (entry.status !== 'ready' || !entry.texture) continue
    if (!isUploadableTextureImage(entry.texture.image)) continue
    entry.texture.needsUpdate = true
  }
}

export function clearTextureCache() {
  for (const [, entry] of byUrl) {
    entry.pendingLoads = []
    closeTextureGpuImage(entry.texture)
    entry.texture.dispose()
  }
  byUrl.clear()
}

registerTextureCacheSizeProvider(() => getTextureCacheSize())
